import "./SuperAdminStorageDashboard.css";

import { Button, useMediaQuery } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { getAllOrgStorageData, getStorageData } from "Config/API/get";

import ApiLoader from "Components/Loader/ApiLoader";
import { ConvertKiloByteToGigaByte } from "Constants/Functions";
import Headers from "Components/Header/Headers";
import ManageStoragePlanPopup from "Components/Popup/ManageStoragePlanPopup/ManageStoragePlanPopup";
import Navigation from "Components/SideNavBar/Navigation";
import blank from "Media/blank-profile-picture.png";
import { toast } from "react-toastify";
import useFullPageLoader from "Hooks/useFullPageLoader";
import { useSelector } from "react-redux";


// Smallest slice (in %) drawn on the pie so tiny non-zero categories stay visible.
const MIN_PIE_SLICE = 1;
// Smallest value shown in the usage breakup for tiny non-zero categories.
const MIN_BREAKUP_VALUE = 0.01;

const FREE_STORAGE_COLOR = "#EFEFEF";
const FREE_STORAGE_BORDER_COLOR = "#D1D1D1";

// Categories in the order they appear in the usage breakup lists.
// `field` is the key in the overall data, `orgField` the key in a single organization data object.
const USAGE_CATEGORIES = [
  { field: "users", orgField: "users", name: "Users & Org", color: "#EDA74C" },
  { field: "chat", orgField: "chat", name: "Chat & Data", color: "#E1A4A4" },
  {
    field: "serviceTicket",
    orgField: "serviceTicket",
    name: "Service Ticketing",
    color: "#ECDC47",
  },
  {
    field: "fileMangement",
    orgField: "fileMangement",
    name: "Files & Folders",
    color: "#87D863",
  },
  {
    field: "portfolio",
    orgField: "portfolio",
    name: "Portfolio & Workbook",
    color: "#6890EE",
  },
  {
    field: "assets",
    orgField: "asset",
    name: "Asset Management",
    color: "rgb(204, 108, 231)",
  },
];

// Order in which slices are drawn on the pie ("free" is the unused storage).
const PIE_ORDER = [
  "users",
  "free",
  "portfolio",
  "fileMangement",
  "serviceTicket",
  "chat",
  "assets",
];

const raiseToMinimum = (value, minimum) =>
  value > 0 && value < minimum ? minimum : value;

const formatWithMinimum = (value) =>
  value > 0 && value < MIN_BREAKUP_VALUE
    ? MIN_BREAKUP_VALUE
    : value?.toFixed(2);

const getPieHeight = ({ isBelow600, isBelow1320 }) => {
  if (isBelow600) return 320;
  if (isBelow1320) return 400;
  return 460;
};

const getPieInnerRadius = ({ isBelow600, isBelow960, isBelow1320 }) => {
  if (isBelow600) return 90;
  if (isBelow960) return 105;
  if (isBelow1320) return 110;
  return 130;
};

const getUsagePercentage = (used, total) => (used * 100) / total;

const getUsageBreakUp = (data) => {
  const totalAvailableSize = data?.totalAvailableSize;
  const usedPercentage = getUsagePercentage(data?.usedSize, totalAvailableSize);

  return [
    {
      name: "Free Storage",
      usedStorage: (
        ConvertKiloByteToGigaByte(totalAvailableSize) -
        ConvertKiloByteToGigaByte(data?.usedSize)
      ).toFixed(2),
      usedPercentage: 100 - formatWithMinimum(usedPercentage),
    },
    ...USAGE_CATEGORIES.map(({ orgField, name }) => ({
      name,
      usedStorage: formatWithMinimum(
        ConvertKiloByteToGigaByte(data?.[orgField])
      ),
      usedPercentage: formatWithMinimum(
        getUsagePercentage(data?.[orgField], totalAvailableSize)
      ),
    })),
  ];
};

const SuperAdminStorageDashboard = () => {
  const token = localStorage.getItem("token");
  const isBelow1320 = useMediaQuery("(max-width:1320px)");
  const isBelow960 = useMediaQuery("(max-width:960px)");
  const isBelow600 = useMediaQuery("(max-width:600px)");
  const [loader, setLoader, resetLoader] = useFullPageLoader();
  const organizationStorageData = useSelector(
    (state) => state.getOrganizationStorageDataReducer
  );
  const totalAvailableSize = organizationStorageData?.totalAvailableSize;
  const totalStorageAvailableInGb = ConvertKiloByteToGigaByte(totalAvailableSize);
  const totalStorageUsedInGb = ConvertKiloByteToGigaByte(
    organizationStorageData?.usedSize
  );
  const toatlStorageUsedPercentage = (
    (totalStorageUsedInGb * 100) /
    totalStorageAvailableInGb
  )?.toFixed(2);

  const categoryPercentages = {};
  USAGE_CATEGORIES.forEach(({ field }) => {
    categoryPercentages[field] = getUsagePercentage(
      organizationStorageData?.[field],
      totalAvailableSize
    );
  });
  const emptyStorage =
    100 - getUsagePercentage(organizationStorageData?.usedSize, totalAvailableSize);

  const pieColors = {
    free: FREE_STORAGE_COLOR,
    ...Object.fromEntries(USAGE_CATEGORIES.map((c) => [c.field, c.color])),
  };
  const storageDataArray = PIE_ORDER.map((key) => ({
    name: " ",
    value:
      key === "free"
        ? emptyStorage
        : raiseToMinimum(categoryPercentages[key], MIN_PIE_SLICE),
    color: pieColors[key],
  }));

  const storageUsageBreakUpArray = [
    {
      name: "Free Storage",
      color: FREE_STORAGE_COLOR,
      borderColor: FREE_STORAGE_BORDER_COLOR,
      usedStorage: (totalStorageAvailableInGb - totalStorageUsedInGb).toFixed(
        2
      ),
      usedPercentage: (100 - toatlStorageUsedPercentage).toFixed(2),
    },
    ...USAGE_CATEGORIES.map(({ field, name, color }) => ({
      name,
      color,
      usedStorage: formatWithMinimum(
        ConvertKiloByteToGigaByte(organizationStorageData?.[field])
      ),
      usedPercentage: formatWithMinimum(categoryPercentages[field]),
    })),
  ];

  const pageLoadingArray = ["", "", "", "", "", "", "", ""];

  const [showUpgradePlanPopup, setShowUpgradePlanPopup] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [isLoading, setIsloading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [selectedOrgStorageData, setSelectedOrgStorageData] = useState({});
  const [allOrgStorageOddData, setAllOrgStorageOddData] = useState([]);
  const [allOrgStorageEvenData, setAllOrgStorageEvenData] = useState([]);

  const selectedOrgStorageUsageBreakUpArray =
    getUsageBreakUp(selectedOrgStorageData);

  useEffect(() => {
    getAllOrganizationStorageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllOrganizationStorageData = () => {
    setIsPageLoading(true);
    const allOrgStorageDataApiResponse = getAllOrgStorageData(token);
    allOrgStorageDataApiResponse.then((res) => {
      setIsPageLoading(false);
      if (res?.data?.status) {
        const evenData = res?.data?.data?.filter((data, index) => {
          return index % 2 === 0;
        });
        setAllOrgStorageEvenData(evenData);

        const oddData = res?.data?.data?.filter((data, index) => {
          return index % 2 !== 0;
        });
        setAllOrgStorageOddData(oddData);
      } else toast(res?.data?.message, { type: "error" });
    });
  };

  const getSelectedOrganizationStorageData = (orgId) => {
    setIsloading(true);
    const getStorageDataApiResponse = getStorageData(token, orgId);
    getStorageDataApiResponse.then((res) => {
      setIsloading(false);
      if (res?.data?.status) {
        setSelectedOrgStorageData(res?.data?.data);
      } else toast(res?.data?.message, { type: "error" });
    });
  };

  const renderOrgCard = (org, avatarAlt) => (
    <div
      key={org?.org_id}
      className="super-admin-storage-dashboard-section-body-content-cards"
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "0 15px",
        }}
      >
        <img
          src={org?.logo || blank}
          alt={avatarAlt}
          onError={(e) => {
            e.target.onerror = null; // Prevent infinite loop
            e.target.src = blank; // Fallback to demo image
          }}
        />
        <div className="super-admin-storage-dashboard-section-body-content-cards-label-container">
          <label>{org?.organization_name}</label>
          <span style={{ color: "#647589" }}>
            Total Data Usage:{" "}
            <label>
              {((org?.totalUsedSize * 100) / org?.totalAvailableSize)?.toFixed(
                2
              )}{" "}
              %
            </label>
          </span>
        </div>
      </div>
      {activeTab === org?.org_id && (
        <div className="expanded-org-storage-details-container">
          {!isLoading ? (
            selectedOrgStorageUsageBreakUpArray.map((breakUp) => (
              <p key={breakUp.name}>
                {breakUp.name}: {breakUp.usedStorage}GB / {breakUp.usedPercentage}
                %
              </p>
            ))
          ) : (
            <div className="expanded-org-storage-details-loader">
              <ApiLoader
                height="35px"
                width="35px"
                borderWidth="7px"
                borderTopWidth="7px"
              />
            </div>
          )}
        </div>
      )}
      <a
        onClick={() => {
          if (activeTab === org?.org_id) return setActiveTab("");
          getSelectedOrganizationStorageData(org?.org_id);
          setActiveTab(org?.org_id);
        }}
      >
        {activeTab === org?.org_id ? "Close" : "View Breakup"}
      </a>
    </div>
  );

  return (
    <>
      <div className="super-admin-storage-dashboard-main">
        <div className="super-admin-storage-dashboard-header">
          <Headers />
        </div>

        <div className="super-admin-storage-dashboard-navigation">
          <Navigation />
        </div>

        <div className="super-admin-storage-dashboard-wrapper">
          <div className="super-admin-storage-dashboard-wrapper-content">
            <aside>
              <header>
                <label>Total Data Usage Chart</label>
                <Button
                  variant="contained"
                  style={{
                    background: "rgb(92, 161, 39)",
                  }}
                  onClick={() => setShowUpgradePlanPopup(true)}
                >
                  MANAGE PLAN
                </Button>
              </header>
              <ResponsiveContainer
                width="100%"
                height={getPieHeight({ isBelow600, isBelow1320 })}
              >
                <PieChart>
                  <Pie
                    data={storageDataArray}
                    innerRadius={getPieInnerRadius({
                      isBelow600,
                      isBelow960,
                      isBelow1320,
                    })}
                    dataKey="value"
                  >
                    {storageDataArray.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="super-admin-storage-dashboard-pie-chat-center-label">
                <label>{toatlStorageUsedPercentage}%</label>
                <label>DATA USED</label>
              </div>
              <div className="super-admin-storage-dashboard-aside-body-content-cards-container">
                {storageUsageBreakUpArray.map((item) => {
                  return (
                    <div
                      key={item.name}
                      className="super-admin-storage-dashboard-aside-body-content-cards"
                    >
                      <div
                        className="super-admin-storage-dashboard-cards-color"
                        style={{
                          backgroundColor: item.color,
                          borderColor: item.borderColor || item.color,
                        }}
                      />
                      <span>
                        {item.name}: {item.usedStorage} GB /{" "}
                        {item.usedPercentage} %
                      </span>
                    </div>
                  );
                })}
              </div>
            </aside>
            <section>
              <header>
                <label>All Organizations Usage Breakup</label>
              </header>
              <div className="super-admin-storage-dashboard-section-body-content-cards-container">
                {!isPageLoading ? (
                  <>
                    <div className="super-admin-storage-dashboard-section-body-first-content">
                      {allOrgStorageEvenData.map((item) =>
                        renderOrgCard(item, "Avatar")
                      )}
                    </div>
                    <div className="super-admin-storage-dashboard-section-body-second-content">
                      {allOrgStorageOddData.map((item) =>
                        renderOrgCard(item, "logo")
                      )}
                    </div>
                  </>
                ) : (
                  pageLoadingArray?.map((_, index) => (
                    <div
                      key={index}
                      style={{ height: "87px" }}
                      className="animation"
                    ></div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
      {loader}
      {showUpgradePlanPopup && (
        <ManageStoragePlanPopup
          hide={setShowUpgradePlanPopup}
          token={token}
          setLoader={setLoader}
          resetLoader={resetLoader}
        />
      )}
    </>
  );
};

export default SuperAdminStorageDashboard;
